import React, { useEffect, useMemo, useState } from 'react';
import { Autocomplete, TextField } from '@mui/material';
import { getAllUsers, type AdminUserRow } from '../../services/usersApi';

export function userDisplayName(u: AdminUserRow): string {
  const full = `${u.firstName || ''} ${u.lastName || ''}`.trim();
  return full || u.nickname || u.email;
}

/** Matches UsersManagement: role Teacher, isTeacher flag, or has assigned students. */
export function userActsAsTeacher(u: AdminUserRow): boolean {
  return (
    u.role === 'Teacher' ||
    Boolean(u.isTeacher) ||
    (Array.isArray(u.studentIds) && u.studentIds.length > 0)
  );
}

function matchesUserSearch(u: AdminUserRow, query: string): boolean {
  const t = query.trim().toLowerCase();
  if (!t) return true;
  const name = `${u.firstName || ''} ${u.lastName || ''}`.trim().toLowerCase();
  return (
    (u.email || '').toLowerCase().includes(t) ||
    (u.nickname || '').toLowerCase().includes(t) ||
    name.includes(t) ||
    (u.role || '').toLowerCase().includes(t) ||
    (u.city || '').toLowerCase().includes(t)
  );
}

export type UserSearchAutocompleteProps = {
  value: AdminUserRow | null;
  onChange: (user: AdminUserRow | null) => void;
  /** When `teacher`, only users who can teach (same rule as Users page). */
  roleFilter?: 'teacher' | 'all';
  /** Hide users already enrolled (by user id). */
  excludeUserIds?: string[];
  label?: string;
  placeholder?: string;
  disabled?: boolean;
  size?: 'small' | 'medium';
  sx?: object;
};

const UserSearchAutocomplete: React.FC<UserSearchAutocompleteProps> = ({
  value,
  onChange,
  roleFilter = 'all',
  excludeUserIds = [],
  label = 'User',
  placeholder = 'Search by name or email',
  disabled = false,
  size = 'small',
  sx,
}) => {
  const [users, setUsers] = useState<AdminUserRow[]>([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    let cancelled = false;
    (async () => {
      setLoading(true);
      try {
        const res = await getAllUsers();
        if (!cancelled && res.data?.users) {
          setUsers(res.data.users);
        }
      } catch {
        if (!cancelled) setUsers([]);
      } finally {
        if (!cancelled) setLoading(false);
      }
    })();
    return () => {
      cancelled = true;
    };
  }, []);

  const options = useMemo(() => {
    const excluded = new Set(excludeUserIds.map(String));
    let list = users.filter((u) => !excluded.has(String(u._id)));
    if (roleFilter === 'teacher') {
      list = list.filter(userActsAsTeacher);
    }
    return list;
  }, [users, roleFilter, excludeUserIds]);

  return (
    <Autocomplete
      size={size}
      sx={{ minWidth: 280, flex: 1, ...sx }}
      options={options}
      value={value}
      onChange={(_e, user) => onChange(user)}
      loading={loading}
      disabled={disabled}
      getOptionLabel={(u) => `${userDisplayName(u)} (${u.email})`}
      isOptionEqualToValue={(a, b) => a._id === b._id}
      filterOptions={(opts, { inputValue }) => opts.filter((u) => matchesUserSearch(u, inputValue))}
      renderInput={(params) => (
        <TextField {...params} label={label} placeholder={placeholder} />
      )}
    />
  );
};

export default UserSearchAutocomplete;
